import {AddOn, SaveAddOnsInput} from "../../../features/digital-products";
import {ProductVariantsFormValues} from "../../../components/offering-form/products/variants/types";
import {computeAddOnDeletions, formValuesToSaveInput} from "./add-ons-form";

/**
 * The write operations `performAddOnSave` needs, as plain async functions.
 * The hook supplies these from React Query mutations; tests supply fakes.
 */
export interface AddOnSaveMutations {
  createAddOns: (input: SaveAddOnsInput) => Promise<AddOn[]>;
  updateAddOns: (input: SaveAddOnsInput) => Promise<AddOn[]>;
  deleteAddOn: (addOnId: string) => Promise<void>;
  deleteAddOnOption: (addOnId: string, optionId: string) => Promise<void>;
}

/**
 * A delete that targets an already-gone entry is the desired end state, so a
 * "not found" is treated as success. This keeps a retry after a partial save
 * from getting wedged re-deleting entries a prior attempt already removed.
 */
export function isNotFoundError(error: unknown): boolean {
  const message = (error as {message?: string} | undefined)?.message ?? "";
  return /not found/i.test(message);
}

/**
 * Persist the add-ons form against the API. Pure orchestration (no React):
 *  1. Explicit, idempotent deletions (removed groups, then options in surviving
 *     groups) — "not found" is swallowed so retries are safe.
 *  2. Partition by group id: existing → PUT (id required), new → POST. Each
 *     fires only when non-empty (the API rejects empty `addOns`).
 * Returns the resulting groups (with server ids); every submitted group is
 * either updated or created, so this is the full current set.
 */
export async function performAddOnSave(
  snapshot: AddOn[],
  values: ProductVariantsFormValues,
  mutations: AddOnSaveMutations
): Promise<AddOn[]> {
  const {removedAddOnIds, removedOptions} = computeAddOnDeletions(snapshot, values);

  for (const addOnId of removedAddOnIds) {
    try {
      await mutations.deleteAddOn(addOnId);
    } catch (error) {
      if (!isNotFoundError(error)) throw error;
    }
  }
  for (const {addOnId, optionId} of removedOptions) {
    try {
      await mutations.deleteAddOnOption(addOnId, optionId);
    } catch (error) {
      if (!isNotFoundError(error)) throw error;
    }
  }

  const input = formValuesToSaveInput(values);
  const existingGroups = input.addOns.filter((addOn) => addOn.id);
  const newGroups = input.addOns.filter((addOn) => !addOn.id);

  let updated: AddOn[] = [];
  let created: AddOn[] = [];

  if (existingGroups.length > 0) {
    updated = await mutations.updateAddOns({
      hideVariantFromProfile: input.hideVariantFromProfile,
      addOns: existingGroups,
    });
  }
  if (newGroups.length > 0) {
    created = await mutations.createAddOns({
      hideVariantFromProfile: input.hideVariantFromProfile,
      addOns: newGroups,
    });
  }

  return [...updated, ...created];
}
